<template>
  <div>
    <div v-if="syncedState.gameState === 'loading'">Loading...</div>
    <template v-else-if="syncedState.gameState === 'finished'">
      <div class="u-pad u-space u-space--large">
        <h2
          class="h2"
        >{{ scores.winners.join(' and ') }} {{ scores.winners.length === 1 ? 'wins' : 'win' }}!</h2>

        <table>
          <thead>
            <tr>
              <th>Player</th>
              <th>Longest Train</th>
              <th>Most Journeys</th>
              <th>Journeys Total</th>
              <th>Trains Total</th>
              <th>Final Score</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in scores.scores" :key="row.id">
              <td>{{ row.winner ? '🥇' : '' }} {{ row.name }}</td>
              <td>{{ row.trainBonus }}</td>
              <td>{{ row.journeyBonus }} ({{ row.completed }})</td>
              <td>{{ row.journeyScore }}</td>
              <td>{{ row.trainScore }}</td>
              <td>
                <strong>{{ row.total }}</strong>
              </td>
            </tr>
          </tbody>
        </table>
        <footer class="u-space--h">
          <button
            type="button"
            class="button"
            @click="$store.dispatch('newGameFromCurrent')"
          >Play Again</button>
          <button type="button" @click="$store.dispatch('notFinished')">Not finished?</button>
        </footer>
      </div>
    </template>
    <div v-else-if="syncedState.gameState === 'in-game'" class="game">
      <header class="game-controls u-split">
        <div class="u-flex u-align-center">
          <h3 class="u-flex u-align-center u-space--h">
            <span>Now playing -</span>
            <strong
              :class="`player-name player-name--${currentPlayer.colour}`"
            >{{ currentPlayer.name }}</strong>
          </h3>

          <button
            v-if="status === 'finishing'"
            type="button"
            class="all-finished"
            @click="$store.dispatch('nextGo')"
          >
            <strong>
              <div>Finished?</div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                class="feather feather-check"
                viewBox="0 0 24 24"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </strong>
          </button>
        </div>

        <span class="u-space--h">
          <button type="button" @click="toggleBoard">Game board</button>
          <strong>Hey {{ you.name }}</strong>
        </span>

        <div class="u-flex u-space--h u-space--large">
          <button type="button" @click="toggleSettings">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="feather feather-settings"
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="3" />
              <path
                d="M19.4 15a1.6 1.6 0 00.3 1.8h0a2 2 0 010 3 2 2 0 01-2.7 0l-.1-.1a1.6 1.6 0 00-1.8-.4 1.6 1.6 0 00-1 1.5v.2a2 2 0 01-2 2 2 2 0 01-2-2v0A1.6 1.6 0 009 19.3a1.6 1.6 0 00-1.8.3h0a2 2 0 01-3 0 2 2 0 010-2.7l.1-.1a1.6 1.6 0 00.4-1.8 1.6 1.6 0 00-1.5-1H3a2 2 0 01-2-2 2 2 0 012-2h0A1.6 1.6 0 004.7 9a1.6 1.6 0 00-.3-1.8h0a2 2 0 010-3 2 2 0 012.7 0l.1.1a1.6 1.6 0 001.8.4H9a1.6 1.6 0 001-1.5V3a2 2 0 012-2 2 2 0 012 2v0a1.6 1.6 0 001 1.6 1.6 1.6 0 001.8-.3h0a2 2 0 013 0 2 2 0 010 2.7l-.1.1a1.6 1.6 0 00-.4 1.8V9a1.6 1.6 0 001.5 1h.2a2 2 0 012 2 2 2 0 01-2 2h0a1.6 1.6 0 00-1.6 1z"
              />
            </svg>
          </button>

          <button type="button" @click="$store.dispatch('sync')">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="feather feather-refresh-cw"
            >
              <path d="M23 4v6h-6M1 20v-6h6" />
              <path d="M3.5 9a9 9 0 0114.9-3.4L23 10M1 14l4.6 4.4A9 9 0 0020.5 15" />
            </svg>
          </button>
        </div>
      </header>

      <div class="game-global u-space u-space--large">
        <CardPicker />
        <JourneyPicker v-if="pondering.length" />
      </div>

      <footer class="game-you u-space u-space--large">
        <h2 class="h2">Your Journeys &amp; Cards</h2>
        <button
          v-if="yourGo && status === 'waiting' && ableToPickUp"
          type="button"
          class="button"
          @click="$store.commit('ponderThreeJourneys')"
        >Pick up new journeys</button>

        <JourneyManager />

        <CardLayer />
      </footer>
    </div>

    <div v-if="showSettings" class="modal">
      <button type="button" class="modal__bg" @click="toggleSettings" />
      <div class="modal__content settings">
        <button type="button" class="u-space--h u-space--large" @click="$store.dispatch('nextGo')">
          <span>Force next go</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="feather feather-arrow-right-circle"
            viewBox="0 0 24 24"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16l4-4-4-4M8 12h8" />
          </svg>
        </button>

        <button
          type="button"
          class="u-space--h u-space--large"
          @click="$store.dispatch('newGameFromCurrent')"
        >
          <span>Start new game</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="feather feather-log-in"
            viewBox="0 0 24 24"
          >
            <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5-5-5M15 12H3" />
          </svg>
        </button>

        <button type="button" class="u-space--h u-space--large" @click="endGameStart">
          <span>End the game</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            class="feather feather-x-circle"
            viewBox="0 0 24 24"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M15 9l-6 6M9 9l6 6" />
          </svg>
        </button>
      </div>
    </div>

    <div v-if="showBoard" class="modal">
      <button type="button" class="modal__bg" @click="toggleBoard" />
      <div class="modal__content">
        <img src="/board.jpg" />
      </div>
    </div>

    <div v-if="showEndGame" class="modal">
      <button type="button" class="modal__bg" @click="toggleEndGame" />
      <div class="modal__content u-space">
        <h3 class="h2">Who had the longest train?</h3>
        <form class="u-space" @submit.prevent="endGame">
          <ul class="u-space">
            <li v-for="player in syncedState.players" :key="player.id">
              <label class="u-space--h u-align-center">
                <input
                  type="checkbox"
                  :value="true"
                  :checked="player.longestTrain"
                  @change="$store.commit('toggleLongestTrain', player.id)"
                />
                <strong>{{ player.name }}</strong>
              </label>
            </li>
          </ul>
          <input type="submit" class="button" value="End the game" />
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import JourneyPicker from "@/components/JourneyPicker.vue";
import CardPicker from "@/components/CardPicker.vue";
import CardLayer from "@/components/CardLayer.vue";
import JourneyManager from "@/components/JourneyManager.vue";

import { mapState, mapGetters } from "vuex";

export default {
  components: {
    JourneyPicker,
    CardPicker,
    CardLayer,
    JourneyManager
  },

  data() {
    return {
      showBoard: false,
      showSettings: false,
      showEndGame: false,
      socket: null
    };
  },

  computed: {
    ...mapState(["syncedState", "pondering", "status", "gameId"]),
    ...mapGetters(["currentPlayer", "ableToPickUp", "yourGo", "you", "scores"])
  },

  mounted() {
    this.$store
      .dispatch("fetch", {
        player: this.$route.params.player,
        game: this.$route.params.id
      })
      .then(() => {
        this.socket = io("https://train-sockets.herokuapp.com");
        // this.socket = io("http://localhost:80");

        this.socket.on("sync", id => {
          if (id == this.gameId) {
            setTimeout(() => {
              this.$store.dispatch("sync");
            }, 100);
          }
        });

        this.socket.on("new-game", ({ oldGameId, newGameId }) => {
          if (oldGameId == this.gameId) {
            setTimeout(() => {
              window.location.href = `/game/${newGameId}`;
            }, 100);
          }
        });

        window.addEventListener("pushSync", () => {
          this.socket.emit("sync", this.gameId);
        });

        window.addEventListener(
          "new-game",
          ({ detail: { oldGameId, newGameId } }) => {
            this.socket.emit("new-game", { oldGameId, newGameId });

            setTimeout(() => {
              window.location.href = `/game/${newGameId}`;
            }, 100);
          }
        );
      });
  },

  beforeDestroy() {
    this.socket && this.socket.close();
  },

  methods: {
    toggleBoard() {
      this.showBoard = !this.showBoard;
    },

    toggleSettings() {
      this.showSettings = !this.showSettings;
    },

    toggleEndGame() {
      this.showEndGame = !this.showEndGame;
    },

    endGameStart() {
      this.toggleSettings();
      this.toggleEndGame();
    },

    endGame() {
      this.$store.dispatch("endGame").then(() => {
        this.toggleEndGame();
      });
    }
  }
};
</script>

<style>
.game {
  padding: 20px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.game-controls {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding: 0 0 10px;
}

.game-global {
  padding: 20px 0;
}

.game-you {
  margin-top: auto;
}

.all-finished {
  background: #333;
  padding: 1rem 2rem;
  z-index: 10;
  position: fixed;
  color: #fff;
  left: 50%;
  top: 50%;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  transform: translate(-50%, -50%);
}

.modal {
  position: fixed;
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
  padding: 1rem;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: 100;
}

.modal__content {
  position: relative;
  z-index: 1;
  background: #fff;
  padding: 1rem;
  max-width: max-content;
  margin: auto;
  width: 100%;
  max-height: 100%;
  overflow-y: scroll;
  min-width: 288px;
}

.modal__bg {
  position: absolute;
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
  background: rgba(39, 35, 34, 0.9);
}

.settings {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 320px;
}

.settings > * {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.settings > * + * {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #ccc;
}
</style>
